'use client';

import { use, useEffect, useState } from 'react';
import Link from 'next/link';
import NavBar from '../../home-page/components/NavBar';
import FooterSection from '../../home-page/components/FooterSection';
import { cityService } from '@/lib/services/cityService';
import { cityServiceService } from '@/lib/services/cityServiceService';
import { generateLocalBusinessSchema, generateBreadcrumbSchema } from '@/lib/utils/metadata';
import { trackEvent } from '@/lib/analytics';
import Breadcrumb from '@/components/ui/Breadcrumb';

const SITE_URL = process.env.NEXT_PUBLIC_SITE_URL || '';

interface CityPageProps {
  params: Promise<{
    city: string;
  }>;
}

interface CityData {
  name: string;
  province: string;
  description: string;
  features: string[];
  coverageAreas?: string[];
  serviceBenefits?: Array<{
    icon: string;
    title: string;
    description: string;
  }>;
  cityTestimonials?: Array<{
    name: string;
    role: string;
    rating: number;
    content: string;
    avatarUrl?: string;
  }>;
  pricingInfo?: {
    baseRate: string;
    perKm: string;
    perHelper: string;
    minimumCharge: string;
    description: string;
    features: string[];
  };
  driverOpportunities?: {
    title: string;
    description: string;
    benefits: string[];
    requirements: string[];
  };
  cityFaqs?: Array<{
    question: string;
    answer: string;
  }>;
  heroImageUrl?: string;
  ctaPrimaryText?: string;
  ctaSecondaryText?: string;
}

interface CityServiceData {
  id: string;
  serviceId: string;
  serviceName?: string;
  serviceSlug?: string;
  isAvailable: boolean;
  citySpecificPricing?: string;
  citySpecificDescription?: string;
  serviceFeatures: string[];
  coverageDetails?: string;
  estimatedDeliveryTime?: string;
  specialNotes?: string;
  bookNowText?: string;
  bookNowLink?: string;
}

const iconMap: Record<string, string> = {
  clock: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
  shield: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
  dollar: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  star: 'M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z',
  truck: 'M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0',
  calendar: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
  users: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z',
  phone: 'M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z',
  map: 'M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7',
  heart: 'M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z',
  graduation: 'M12 14l9-5-9-5-9 5 9 5z M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z',
  leaf: 'M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z',
};

export default function CityPage({ params }: CityPageProps) {
  const resolvedParams = use(params);
  const [city, setCity] = useState<CityData | null>(null);
  const [loading, setLoading] = useState(true);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
  const [cityServicesData, setCityServicesData] = useState<CityServiceData[]>([]);

  useEffect(() => {
    const loadCity = async () => {
      try {
        const data = await cityService.getBySlug(resolvedParams.city);
        if (data) {
          setCity({
            name: data.name,
            province: data.province,
            description: data.description,
            features: data.features,
            coverageAreas: data.coverageAreas,
            serviceBenefits: data.serviceBenefits,
            cityTestimonials: data.cityTestimonials,
            pricingInfo: data.pricingInfo,
            driverOpportunities: data.driverOpportunities,
            cityFaqs: data.cityFaqs,
            heroImageUrl: data.heroImageUrl,
            ctaPrimaryText: data.ctaPrimaryText,
            ctaSecondaryText: data.ctaSecondaryText,
          });

          // Track city page view with city name, slug, and geographic data
          trackEvent('city_page_view', {
            city_name: data.name,
            city_slug: resolvedParams.city,
            province: data.province,
            page_location: window.location.href,
            page_title: `GoGedit in ${data.name}, ${data.province}`,
          });

          // Load city-specific services
          const services = await cityServiceService.getByCitySlug(resolvedParams.city);
          setCityServicesData(services.map(s => ({
            id: s.id,
            serviceId: s.serviceId,
            serviceName: s.serviceName,
            serviceSlug: s.serviceSlug,
            isAvailable: s.isAvailable,
            citySpecificPricing: s.citySpecificPricing,
            citySpecificDescription: s.citySpecificDescription,
            serviceFeatures: s.serviceFeatures || [],
            coverageDetails: s.coverageDetails,
            estimatedDeliveryTime: s.estimatedDeliveryTime,
            specialNotes: s.specialNotes,
            bookNowText: s.bookNowText,
            bookNowLink: s.bookNowLink,
          })));
        }
      } catch (error: any) {
        console.error('Failed to load city:', error.message);
      } finally {
        setLoading(false);
      }
    };

    loadCity();
  }, [resolvedParams.city]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <NavBar />
        <div className="pt-24 pb-16 flex items-center justify-center">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-brand-purple border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-body-text">Loading...</p>
          </div>
        </div>
        <FooterSection />
      </div>
    );
  }

  if (!city) {
    return (
      <div className="min-h-screen bg-white">
        <NavBar />
        <div className="pt-24 pb-16 text-center">
          <h1 className="text-4xl font-bold text-navy-dark">City Not Found</h1>
          <Link href="/home-page" className="text-brand-purple hover:underline mt-4 inline-block">
            Return to Home
          </Link>
        </div>
        <FooterSection />
      </div>
    );
  }

  const localBusinessSchema = generateLocalBusinessSchema({
    name: city.name,
    description: city.description,
    city: city.name,
    province: city.province,
    url: `${SITE_URL}/cities/${resolvedParams.city}`,
  });

  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', url: `${SITE_URL}/home-page` },
    { name: 'Cities', url: `${SITE_URL}/home-page#cities` },
    { name: city.name, url: `${SITE_URL}/cities/${resolvedParams.city}` },
  ]);

  const faqSchema = city.cityFaqs && city.cityFaqs.length > 0 ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: city.cityFaqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  } : null;

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(localBusinessSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      {faqSchema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
        />
      )}
      <div className="min-h-screen bg-white">
        <NavBar />

        <div className="pt-24 pb-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-6">
              <Breadcrumb items={[{ label: 'Cities', href: '/home-page#cities' }, { label: city.name }]} />
            </div>

            {/* Hero Section */}
            <div className="text-center mb-16">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-navy-dark mb-4 break-words">
                GoGedit in {city.name}, {city.province}
              </h1>
              <p className="text-lg sm:text-xl text-body-text max-w-3xl mx-auto">
                On-Demand Moving Services in {city.name}
              </p>
            </div>

            {/* About & Features Section */}
            <div className="grid md:grid-cols-2 gap-8 lg:gap-12 mb-16">
              <div>
                <h2 className="text-3xl font-bold text-navy-dark mb-6">About Our Service</h2>
                <p className="text-lg text-body-text mb-6">{city.description}</p>
                <p className="text-lg text-body-text">
                  We won&apos;t move you or your house - just everything else. Perfect for furniture pickups,
                  appliance deliveries, marketplace purchases, and more.
                </p>
              </div>
              <div className="bg-purple-light rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-navy-dark mb-6">Why Choose GoGedit?</h3>
                <ul className="space-y-3">
                  {city?.features?.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <svg
                        className="w-6 h-6 text-brand-green mr-3 flex-shrink-0 mt-0.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-body-text">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Coverage Areas */}
            {city?.coverageAreas && city.coverageAreas.length > 0 && (
              <div className="mb-16">
                <h2 className="text-3xl font-bold text-navy-dark mb-8 text-center">Areas We Serve in {city.name}</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4">
                  {city.coverageAreas.map((area, index) => (
                    <div key={index} className="bg-gray-50 rounded-lg p-3 md:p-4 text-center">
                      <svg
                        className="w-6 h-6 text-brand-purple mx-auto mb-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                      <p className="text-xs sm:text-sm font-medium text-navy-dark break-words">{area}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* City-Specific Services Section */}
            {cityServicesData && cityServicesData.length > 0 && (
              <div className="mb-16">
                <h2 className="text-3xl font-bold text-navy-dark mb-8 text-center">
                  Our Services in {city?.name}
                </h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {cityServicesData.map((service) => (
                    <div key={service.id} className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-brand-purple transition-colors">
                      <div className="mb-4">
                        <h3 className="text-xl font-bold text-navy-dark mb-2">{service.serviceName}</h3>
                        <p className="text-brand-purple font-semibold text-lg">{service.citySpecificPricing}</p>
                      </div>
                      
                      {service.citySpecificDescription && (
                        <p className="text-body-text mb-4">{service.citySpecificDescription}</p>
                      )}

                      {service.serviceFeatures && service.serviceFeatures.length > 0 && (
                        <div className="mb-4">
                          <h4 className="font-semibold text-navy-dark mb-2 text-sm">Features:</h4>
                          <ul className="space-y-2">
                            {service.serviceFeatures.map((feature, index) => (
                              <li key={index} className="flex items-start text-sm">
                                <svg
                                  className="w-5 h-5 text-brand-green mr-2 flex-shrink-0 mt-0.5"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                <span className="text-body-text">{feature}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {(service.coverageDetails || service.estimatedDeliveryTime) && (
                        <div className="border-t pt-4 mt-4 space-y-2">
                          {service.estimatedDeliveryTime && (
                            <div className="flex items-center text-sm">
                              <svg className="w-5 h-5 text-brand-purple mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <span className="text-body-text">{service.estimatedDeliveryTime}</span>
                            </div>
                          )}
                          {service.coverageDetails && (
                            <div className="flex items-start text-sm">
                              <svg className="w-5 h-5 text-brand-purple mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              </svg>
                              <span className="text-body-text">{service.coverageDetails}</span>
                            </div>
                          )}
                        </div>
                      )}

                      {service.specialNotes && (
                        <div className="mt-4 p-3 bg-purple-light rounded-lg">
                          <p className="text-xs text-body-text">{service.specialNotes}</p>
                        </div>
                      )}

                      <div className="mt-4">
                        <Link
                          href={service.bookNowLink || '/user-signup'}
                          onClick={() => {
                            trackEvent('service_click', {
                              service_name: service.serviceName,
                              service_id: service.serviceId,
                              city_name: city?.name,
                              city_slug: resolvedParams.city,
                              pricing: service.citySpecificPricing,
                              action: 'book_now',
                            });
                          }}
                          className="block w-full text-center bg-brand-purple text-white py-2 rounded-lg hover:bg-opacity-90 transition-colors"
                        >
                          {service.bookNowText || 'Book Now'}
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Service Benefits */}
            {city?.serviceBenefits && city.serviceBenefits.length > 0 && (
              <div className="mb-16">
                <h2 className="text-3xl font-bold text-navy-dark mb-8 text-center">Service Benefits</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
                  {city.serviceBenefits.map((benefit, index) => (
                    <div key={index} className="text-center">
                      <div className="w-16 h-16 bg-brand-purple rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d={iconMap[benefit.icon] || iconMap.star}
                          />
                        </svg>
                      </div>
                      <h3 className="text-xl font-bold text-navy-dark mb-2">{benefit.title}</h3>
                      <p className="text-body-text">{benefit.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Pricing Information */}
            {city?.pricingInfo && (
              <div className="mb-16">
                <h2 className="text-3xl font-bold text-navy-dark mb-8 text-center">Transparent Pricing</h2>
                <div className="bg-gradient-to-br from-brand-purple to-brand-green rounded-2xl p-6 md:p-8 text-white">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-6">
                    <div className="text-center">
                      <p className="text-xs sm:text-sm opacity-90 mb-1">Base Rate</p>
                      <p className="text-xl sm:text-2xl md:text-3xl font-bold break-words">{city.pricingInfo.baseRate}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs sm:text-sm opacity-90 mb-1">Per Kilometer</p>
                      <p className="text-xl sm:text-2xl md:text-3xl font-bold break-words">{city.pricingInfo.perKm}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs sm:text-sm opacity-90 mb-1">Per Helper</p>
                      <p className="text-xl sm:text-2xl md:text-3xl font-bold break-words">{city.pricingInfo.perHelper}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs sm:text-sm opacity-90 mb-1">Minimum</p>
                      <p className="text-xl sm:text-2xl md:text-3xl font-bold break-words">{city.pricingInfo.minimumCharge}</p>
                    </div>
                  </div>
                  <p className="text-center mb-6 opacity-90 text-sm sm:text-base">{city.pricingInfo.description}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {city.pricingInfo.features?.map((feature, index) => (
                      <div key={index} className="flex items-center">
                        <svg className="w-5 h-5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <span className="text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Driver Opportunities */}
            {city?.driverOpportunities && (
              <div className="mb-16">
                <div className="bg-brand-green rounded-2xl p-8 md:p-12 text-white">
                  <h2 className="text-3xl font-bold mb-4 text-center">{city.driverOpportunities.title}</h2>
                  <p className="text-xl text-center mb-8 opacity-90">{city.driverOpportunities.description}</p>
                  <div className="grid md:grid-cols-2 gap-8">
                    <div>
                      <h3 className="text-xl font-bold mb-4">Benefits</h3>
                      <ul className="space-y-2">
                        {city.driverOpportunities.benefits?.map((benefit, index) => (
                          <li key={index} className="flex items-start">
                            <svg className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                              <path
                                fillRule="evenodd"
                                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                clipRule="evenodd"
                              />
                            </svg>
                            <span>{benefit}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-4">Requirements</h3>
                      <ul className="space-y-2">
                        {city.driverOpportunities.requirements?.map((requirement, index) => (
                          <li key={index} className="flex items-start">
                            <svg className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                              <path
                                fillRule="evenodd"
                                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                clipRule="evenodd"
                              />
                            </svg>
                            <span>{requirement}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <div className="text-center mt-8">
                    <Link
                      href="/be-a-driver"
                      onClick={() => {
                        trackEvent('driver_signup_click', { 
                          city: city.name,
                          city_slug: resolvedParams.city,
                          province: city.province,
                        });
                        trackEvent('geographic_conversion', {
                          conversion_type: 'driver_application',
                          city_name: city.name,
                          city_slug: resolvedParams.city,
                          province: city.province,
                          page_location: window.location.href,
                        });
                      }}
                      className="inline-block bg-white text-brand-green px-8 py-4 rounded-full text-lg font-medium hover:bg-opacity-90 transition-all"
                    >
                      Apply to Drive
                    </Link>
                  </div>
                </div>
              </div>
            )}

            {/* FAQ Section */}
            {city?.cityFaqs && city.cityFaqs.length > 0 && (
              <div className="mb-16">
                <h2 className="text-3xl font-bold text-navy-dark mb-8 text-center">
                  Frequently Asked Questions
                </h2>
                <div className="max-w-3xl mx-auto space-y-4">
                  {city.cityFaqs.map((faq, index) => (
                    <div key={index} className="bg-white rounded-lg shadow-md overflow-hidden">
                      <button
                        onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                        className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                      >
                        <span className="font-semibold text-navy-dark">{faq.question}</span>
                        <svg
                          className={`w-5 h-5 text-brand-purple transform transition-transform ${
                            openFaqIndex === index ? 'rotate-180' : ''
                          }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      {openFaqIndex === index && (
                        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                          <p className="text-body-text">{faq.answer}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* CTA Section */}
            <div className="bg-gradient-to-r from-brand-purple to-brand-green rounded-2xl p-6 sm:p-8 md:p-12 text-center text-white mb-16">
              <h2 className="text-2xl sm:text-3xl font-bold mb-4">Ready to GoGedit in {city.name}?</h2>
              <p className="text-base sm:text-xl mb-8 opacity-90">
                Join thousands of satisfied customers who trust GoGedit for their moving needs
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  href="/be-a-user"
                  onClick={() => {
                    trackEvent('signup_click', { 
                      type: 'user', 
                      city: city.name,
                      city_slug: resolvedParams.city,
                      province: city.province,
                    });
                    trackEvent('inquiry_submission', {
                      inquiry_type: 'user_signup',
                      city_name: city.name,
                      city_slug: resolvedParams.city,
                      province: city.province,
                      cta_location: 'bottom_cta',
                    });
                    trackEvent('geographic_conversion', {
                      conversion_type: 'user_signup',
                      city_name: city.name,
                      city_slug: resolvedParams.city,
                      province: city.province,
                      page_location: window.location.href,
                    });
                  }}
                  className="bg-white text-brand-purple px-8 py-4 rounded-full text-lg font-medium hover:bg-opacity-90 transition-all"
                >
                  {city.ctaPrimaryText || 'Sign Up as User'}
                </Link>
                <Link
                  href="/be-a-driver"
                  onClick={() => {
                    trackEvent('signup_click', { 
                      type: 'driver', 
                      city: city.name,
                      city_slug: resolvedParams.city,
                      province: city.province,
                    });
                    trackEvent('inquiry_submission', {
                      inquiry_type: 'driver_signup',
                      city_name: city.name,
                      city_slug: resolvedParams.city,
                      province: city.province,
                      cta_location: 'bottom_cta',
                    });
                    trackEvent('geographic_conversion', {
                      conversion_type: 'driver_application',
                      city_name: city.name,
                      city_slug: resolvedParams.city,
                      province: city.province,
                      page_location: window.location.href,
                    });
                  }}
                  className="bg-brand-green text-white px-8 py-4 rounded-full text-lg font-medium hover:bg-opacity-90 transition-all border-2 border-white"
                >
                  {city.ctaSecondaryText || 'Become a Driver'}
                </Link>
              </div>
            </div>
          </div>
        </div>

        <FooterSection />
      </div>
    </>
  );
}